/**
 * AI Service
 *
 * Handles AI-powered code generation, explanation, and transformation.
 * Calls the backend API which uses AWS Bedrock/Claude.
 */

import { getAPIBaseUrl } from '../environment'

/**
 * Call the AI chat endpoint
 *
 * @param {string} code - The code to analyze
 * @param {string} prompt - The user prompt
 * @param {string} purpose - Purpose: code_explain, generate_java, refactor, custom
 * @param {object} context - Additional context
 * @returns {Promise<object>} - AI response
 */
async function callAI(code, prompt, purpose, context = {}) {
  const token = localStorage.getItem('modernizeit_token')
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  // Lift tenant context to top-level fields for credential lookup
  const { scout_account_id, user_id, application_name, ...restContext } = context
  const response = await fetch(`${getAPIBaseUrl()}/ai/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      prompt,
      code,
      purpose,
      context: restContext,
      ...(scout_account_id && { scout_account_id }),
      ...(user_id && { user_id }),
      ...(application_name && { application_name }),
    })
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `API error: ${response.status}`)
  }

  return response.json()
}

/**
 * Generate Java code from COBOL section
 *
 * @param {string} cobolCode - The COBOL source code
 * @param {string} sectionId - The section identifier
 * @param {string} sectionName - The section name
 * @param {object} context - Additional context (surrounding code, data structures, etc.)
 * @returns {Promise<object>} - Generated code, explanation, and confidence
 */
export async function generateJavaFromCobol(cobolCode, sectionId, sectionName, context = {}) {
  const result = await callAI(
    cobolCode,
    `Convert this COBOL code to modern Java. Section: ${sectionName}`,
    'generate_java',
    {
      ...context,
      section: sectionName,
      language: 'cobol'
    }
  )

  return {
    success: true,
    code: result.code || result.response,
    explanation: `Generated Java code for ${sectionName}`,
    confidence: result.confidence || 0.85,
    sectionId,
    sectionName,
    log_id: result.log_id,
    duration_ms: result.duration_ms,
    timestamp: new Date().toISOString()
  }
}

/**
 * Explain COBOL code section
 *
 * @param {string} cobolCode - The COBOL source code
 * @param {string} sectionId - The section identifier
 * @param {string} sectionName - The section name
 * @returns {Promise<object>} - Explanation of the code
 */
export async function explainCobolCode(cobolCode, sectionId, sectionName) {
  const result = await callAI(
    cobolCode,
    `Explain what this code does. Section: ${sectionName}`,
    'code_explain',
    {
      section: sectionName,
      language: 'cobol'
    }
  )

  return {
    success: true,
    explanation: result.response,
    sectionId,
    sectionName,
    log_id: result.log_id,
    duration_ms: result.duration_ms,
    timestamp: new Date().toISOString()
  }
}

/**
 * Optimize/improve generated Java code
 *
 * @param {string} javaCode - The current Java code
 * @param {string} sectionId - The section identifier
 * @param {string} sectionName - The section name
 * @returns {Promise<object>} - Optimized code and suggestions
 */
export async function optimizeJavaCode(javaCode, sectionId, sectionName) {
  const result = await callAI(
    javaCode,
    `Suggest refactoring improvements for this code. Section: ${sectionName}`,
    'refactor',
    {
      section: sectionName,
      language: 'java'
    }
  )

  return {
    success: true,
    code: result.code,
    explanation: result.response,
    suggestions: result.suggestions || [],
    sectionId,
    sectionName,
    log_id: result.log_id,
    duration_ms: result.duration_ms,
    timestamp: new Date().toISOString()
  }
}

/**
 * Custom AI prompt for code transformation
 *
 * @param {string} code - The source code
 * @param {string} prompt - User's custom prompt
 * @param {string} sectionId - The section identifier
 * @param {string} sectionName - The section name
 * @param {object} context - Additional context
 * @returns {Promise<object>} - AI response
 */
export async function customPrompt(code, prompt, sectionId, sectionName, context = {}) {
  const result = await callAI(
    code,
    prompt,
    'custom',
    {
      ...context,
      section: sectionName
    }
  )

  return {
    success: true,
    response: result.response,
    code: result.code,
    sectionId,
    sectionName,
    log_id: result.log_id,
    duration_ms: result.duration_ms,
    timestamp: new Date().toISOString()
  }
}

/**
 * Clean up code formatting and indentation using AI.
 * Sends the source to the backend clean_code purpose which fixes indentation,
 * brace placement, spacing, and import ordering without changing logic.
 *
 * @param {string} code - Source code to clean
 * @param {string} language - Programming language label (e.g. 'Java', 'Python')
 * @param {string} filename - Source filename for context
 * @returns {Promise<{success, cleaned, log_id, duration_ms, timestamp}>}
 */
export async function cleanCode(code, language, filename) {
  const result = await callAI(
    code,
    `Clean the formatting and indentation of this ${language} source file. Return only the reformatted code.`,
    'clean_code',
    { language, file: filename, section: filename }
  )

  const cleaned = result.code || result.response
  if (!cleaned || !cleaned.trim()) {
    throw new Error('AI Clean Code returned an empty response')
  }

  return {
    success: true,
    cleaned,
    log_id: result.log_id,
    duration_ms: result.duration_ms,
    timestamp: new Date().toISOString()
  }
}

export default {
  generateJavaFromCobol,
  explainCobolCode,
  optimizeJavaCode,
  customPrompt,
  cleanCode
}
